import { authenticatedFetch } from "./http";

export type QualityDisposition =
  "RELEASED" | "REPACKAGING" | "RETURN_TO_SUPPLIER" | "SCRAPPED";
export type DerogationDisposition =
  "ACCEPT_NEW_EXPIRY" | "SUBMIT_TO_CUSTOMER" | "SUBMIT_TO_SUPPLIER" |
  "RETURN_TO_SUPPLIER" | "QUARANTINE";

export interface QualityInspection {
  id: string;
  inspectionNumber: string;
  handlingUnitId: string;
  huNumber: string;
  asnNumber: string | null;
  receiptNumber: string | null;
  clientCode: string | null;
  clientName: string | null;
  itemCodes: string;
  serialNumbers: string | null;
  quantity: number;
  uom: string;
  warehouseId: string;
  warehouseCode: string;
  processPointCode: string;
  reasonCode: string;
  reportedComment: string;
  status: "WAITING_TRANSFER" | "OPEN" | "IN_PROGRESS" |
    "COMPLETED" | "CANCELLED";
  assignedUserId: string | null;
  assignedUserName: string | null;
  disposition: QualityDisposition | DerogationDisposition | null;
  dispositionComment: string | null;
  originalExpirationDate: string | null;
  extensionDays: number | null;
  requestedExpirationDate: string | null;
  customerRequestStatus: string | null;
  derogationType: string | null;
  workflowState: "INTERNAL_REVIEW" | "PARTNER_REVIEW" |
    "PENDING_EXECUTION" | "COMPLETED" | "CANCELLED" | null;
  currentValidationLevel: number | null;
  currentBusinessPartnerId: string | null;
  currentBusinessPartnerCode: string | null;
  currentBusinessPartnerName: string | null;
  currentBusinessPartnerKind: string | null;
  proposalAdjustmentAllowed: boolean | null;
  partnerDecision: "ACCEPTED" | "REJECTED" | null;
  partnerDecisionComment: string | null;
  decidedExtensionDays: number | null;
  decidedExpirationDate: string | null;
  partnerDecidedAt: string | null;
  directApplication: boolean | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  version: number;
}

export function loadQualityInspections(): Promise<QualityInspection[]> {
  return authenticatedFetch<QualityInspection[]>("/api/v1/quality/inspections");
}

export function loadDerogations(): Promise<QualityInspection[]> {
  return authenticatedFetch<QualityInspection[]>("/api/v1/derogations");
}

export function loadDerogation(id: string): Promise<QualityInspection> {
  return authenticatedFetch<QualityInspection>(`/api/v1/derogations/${id}`);
}

export function claimDerogation(inspection: QualityInspection): Promise<QualityInspection> {
  return authenticatedFetch<QualityInspection>(`/api/v1/derogations/${inspection.id}/claim`, {
    method: "POST", body: JSON.stringify({ version: inspection.version })
  });
}

export function decideDerogation(inspection: QualityInspection,
  disposition: DerogationDisposition, comment: string,
  extensionDays?: number | null
): Promise<QualityInspection> {
  return authenticatedFetch<QualityInspection>(`/api/v1/derogations/${inspection.id}/disposition`, {
    method: "POST", body: JSON.stringify({ version: inspection.version,
      disposition, extensionDays, comment })
  });
}

export function loadPortalDerogations(): Promise<QualityInspection[]> {
  return authenticatedFetch<QualityInspection[]>("/api/v1/portal/derogations");
}

export function decidePortalDerogation(
  inspection: QualityInspection,
  decision: "ACCEPTED" | "REJECTED",
  comment: string,
  extensionDays?: number | null
): Promise<QualityInspection> {
  return authenticatedFetch<QualityInspection>(
    `/api/v1/portal/derogations/${inspection.id}/decision`,
    { method: "POST", body: JSON.stringify({
      decision, extensionDays, comment, version: inspection.version
    }) }
  );
}

export function loadQualityInspection(id: string): Promise<QualityInspection> {
  return authenticatedFetch<QualityInspection>(`/api/v1/quality/inspections/${id}`);
}

export function claimQualityInspection(
  inspection: QualityInspection
): Promise<QualityInspection> {
  return authenticatedFetch<QualityInspection>(
    `/api/v1/quality/inspections/${inspection.id}/claim`,
    { method: "POST", body: JSON.stringify({ version: inspection.version }) }
  );
}

export function releaseQualityInspection(
  inspection: QualityInspection,
  departureProcessPointId: string,
  comment: string
): Promise<QualityInspection> {
  return decideQualityInspection(
    inspection, "RELEASED", departureProcessPointId, comment
  );
}

export function decideQualityInspection(
  inspection: QualityInspection,
  disposition: QualityDisposition,
  departureProcessPointId: string | null,
  comment: string
): Promise<QualityInspection> {
  return authenticatedFetch<QualityInspection>(
    `/api/v1/quality/inspections/${inspection.id}/disposition`,
    {
      method: "POST",
      body: JSON.stringify({
        version: inspection.version,
        disposition,
        departureProcessPointId,
        comment
      })
    }
  );
}
